import * as React from 'react';

import {
  Button,
  Col,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  Select,
  TextArea,
} from '@rematter/ui-web';
import { LucideLockOpen } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

type PeriodUnlockDialogProps = {
  periodName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, expiresAt?: string) => Promise<void>;
};

const DURATION_OPTIONS = [
  { key: 'none', label: 'No auto-relock' },
  { key: '1h', label: '1 hour' },
  { key: '4h', label: '4 hours' },
  { key: '8h', label: '8 hours' },
  { key: '24h', label: '24 hours' },
  { key: '48h', label: '48 hours' },
  { key: '7d', label: '7 days' },
];

const computeExpiresAt = (duration: string): string | undefined => {
  if (duration === 'none') return undefined;

  const now = new Date();
  const multipliers: Record<string, number> = {
    '1h': 1 * 60 * 60 * 1000,
    '4h': 4 * 60 * 60 * 1000,
    '8h': 8 * 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '48h': 48 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
  };

  const ms = multipliers[duration];
  if (!ms) return undefined;

  return new Date(now.getTime() + ms).toISOString();
};

export const PeriodUnlockDialog = ({
  periodName,
  isOpen,
  onClose,
  onConfirm,
}: PeriodUnlockDialogProps) => {
  const t = useTranslations();

  const [reason, setReason] = React.useState('');
  const [duration, setDuration] = React.useState('none');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );

  const handleClose = React.useCallback(() => {
    setReason('');
    setDuration('none');
    setValidationError(null);
    onClose();
  }, [onClose]);

  const handleConfirm = React.useCallback(async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setValidationError(
        t.periodsListView.unlockDialog.validation.reasonRequired,
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setValidationError(null);
      const expiresAt = computeExpiresAt(duration);
      await onConfirm(trimmedReason, expiresAt);
    } finally {
      setIsSubmitting(false);
    }
  }, [reason, duration, onConfirm, t]);

  const handleReasonChange = React.useCallback((value: string) => {
    setReason(value);
    setValidationError(null);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      setReason('');
      setDuration('none');
      setValidationError(null);
    }
  }, [isOpen]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      className="@PeriodUnlockDialog"
    >
      <DialogContent className="max-w-md min-w-[380px]" closeButton={false}>
        <DialogHeader className="mr-0">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <LucideLockOpen className="size-5 shrink-0 text-neutral/70" />
            {t.periodsListView.unlockDialog.title}
          </div>
          <div className="text-sm text-neutral/75">
            {t.periodsListView.unlockDialog.description(periodName)}
          </div>
        </DialogHeader>

        <Col className="px-4" gap4>
          <TextArea
            label={t.periodsListView.unlockDialog.reasonLabel}
            placeholder={t.periodsListView.unlockDialog.reasonPlaceholder}
            value={reason}
            onChange={handleReasonChange}
            isRequired
            isInvalid={!!validationError}
            errorMessage={validationError ?? undefined}
            rows={3}
            isDisabled={isSubmitting}
          />

          <Select
            label={t.periodsListView.unlockDialog.durationLabel}
            value={duration}
            onChange={(key) => setDuration(key)}
            options={DURATION_OPTIONS}
            isDisabled={isSubmitting}
          />
        </Col>

        <div className="flex justify-end gap-2 px-4 py-4">
          <Button
            variant="text"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.periodsListView.unlockDialog.cancel}
          </Button>
          <Button
            variant="solid"
            color="primary"
            onPress={handleConfirm}
            isDisabled={isSubmitting || !reason.trim()}
            isLoading={isSubmitting}
          >
            {t.periodsListView.unlockDialog.confirm}
          </Button>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
